<!-- Wallet Waitlist -->
<!-- https://wallet.hello.coop/waitlist -->

<script>
    import { handleUnrecoverableError } from '$utils/errors.js'
    import { _, locale, isLoading } from 'svelte-i18n'
    import { isValidEmail } from '$utils/email.js'
    import WaitlistErrorModal from '$lib/modal/error/WaitlistErrorModal.svelte'
    import { global } from '$src/state.svelte.js'
    import SpinnerIcon from '$lib/icon/SpinnerIcon.svelte'
    import { getDomainDiscovery, getWaitlist } from '$utils/api.js'
    import { SLIDE_ANIM_MS, DISCO_DEBOUNCE_MS } from '$src/constants.js'
    import { slide } from 'svelte/transition'
    import { emailDomains } from '$utils/email.js'
    import { onMount } from 'svelte'
    import { cleanUrl } from '$utils/common.js'
    import { preventDefault } from '$utils/common.js'
    import { postWaitlist } from '$utils/api.js'
    import { Confetti } from 'svelte-confetti'

    let invalidParams = $state(false)
    let signedUp = $state(false)
    let signUpError = $state(null)
    let email = $state('')
    let isInvalidEmail = $state(false)
    let discoRes = $state(null)
    let showProviderPrompt = $state(false)
    let ajaxRequestSend = $state(false)
    let waitListLabel = $state('')
    let returnUri = $state('')
    const VALID_WAITLIST_LABELS = ['GitHub Offboarding', 'Hellō Lifecycle']
    const isValidReturnUri = (uri) => {
        try {
            new URL(uri)
            return true
        } catch {
            return false
        }
    }

    onMount(async () => {
        try {
            global.spinner = true
            const params = new URLSearchParams(window.location.search)
            waitListLabel =
                params.get('waitlist_label') ||
                sessionStorage.getItem('waitlist_label')
            returnUri =
                params.get('return_uri') || sessionStorage.getItem('return_uri')
            if (
                VALID_WAITLIST_LABELS.includes(waitListLabel) &&
                isValidReturnUri(returnUri)
            ) {
                // we call this to create a session
                // required for the domain discovery api
                await getWaitlist()

                // overwrite session storage if params are valid
                sessionStorage.setItem('waitlist_label', waitListLabel)
                sessionStorage.setItem('return_uri', returnUri)
            } else {
                invalidParams = true
                // clean up session storage if params are invalid
                sessionStorage.removeItem('waitlist_label')
                sessionStorage.removeItem('return_uri')
            }
        } finally {
            cleanUrl()
            global.spinner = false
        }
    })

    let timer
    let checkEmailProviderAjax = $state(false)
    let discoVersion = 0
    async function checkEmail(e) {
        discoVersion++ // bump version to invalidate any in-flight lookups
        const currentVersion = discoVersion
        discoRes = null
        isInvalidEmail = false
        signUpError = null
        const domain = e.target.value.split('@')[1]
        if (timer) clearTimeout(timer)
        // Reset checkEmailProviderAjax if no domain or invalid email
        if (!domain || !isValidEmail(email)) {
            checkEmailProviderAjax = false
            return
        }
        if (domain && isValidEmail(email)) {
            for (const key in emailDomains) {
                if (emailDomains[key].includes(domain)) {
                    showProviderPrompt = true
                    discoRes = { slug: key }
                    checkEmailProviderAjax = false
                    return
                }
            }
            // Set checkEmailProviderAjax to true immediately to disable the button during debounce
            checkEmailProviderAjax = true
            timer = setTimeout(async () => {
                const result = await getDomainDiscovery(domain)
                if (discoVersion !== currentVersion) {
                    checkEmailProviderAjax = false
                    return // stale result
                }
                discoRes = result
                // Check for disposable email error
                if (
                    discoRes.error &&
                    ['DISPOSABLE_EMAIL', 'NO_MX_RECORD'].includes(
                        discoRes.error.message,
                    )
                ) {
                    isInvalidEmail = true
                    checkEmailProviderAjax = false
                    return
                }
                // Reset disposable email state
                isInvalidEmail = false
                checkEmailProviderAjax = false
            }, DISCO_DEBOUNCE_MS)
        }
    }

    async function submit() {
        try {
            // Prevent sending verification code while provider discovery is in progress
            if (checkEmailProviderAjax) {
                return
            }
            ajaxRequestSend = true
            await postWaitlist(email, waitListLabel)
            signedUp = true
            setTimeout(() => {
                window.location.href = returnUri
            }, 5000)
        } catch {
            ajaxRequestSend = false
            signUpError = true
        }
    }
</script>

<svelte:window
    onerror={handleUnrecoverableError}
    onunhandledrejection={handleUnrecoverableError}
/>

{#if global.serverErrorModal || global.clientErrorModal}
    <WaitlistErrorModal />
{/if}

{#if !$locale || $isLoading || global.spinner}
    <div class="spinner"></div>
{:else}
    {#if !invalidParams}
        <header
            class="bg-charcoal flex h-12 w-full flex-shrink-0 items-center justify-center text-xl font-semibold text-white dark:text-[#D4D4D4]"
        >
            <a
                href="https://www.hello.coop/"
                class="hover:underline"
                target="_blank">Hellō</a
            >
        </header>
    {/if}
    <main class="mx-auto mt-8 max-w-lg px-6 pb-10 text-center">
        {#if invalidParams}
            <h1 class="text-lg">Nothing to see here</h1>
            <p class="my-8 opacity-80">
                It looks like you navigated to this page directly.<br />
                This page is only intended to be opened by an application.
            </p>
            <button onclick={window.history.back()} class="underline"
                >Go back</button
            >
        {:else if signedUp}
            {@const hostname = new URL(returnUri).hostname}
            <div
                class="pointer-events-none fixed -top-3 left-0 flex h-screen w-screen justify-center overflow-hidden"
            >
                <Confetti
                    x={[-5, 5]}
                    y={[0, 0.1]}
                    delay={[500, 2000]}
                    duration="2500"
                    amount="250"
                    fallDistance="100vh"
                />
            </div>

            <h1 class="text-center text-2xl font-medium leading-10">
                {@html $_(
                    'Thank you for signing up for the<br/>{waitListLabel} waitlist!',
                    {
                        values: {
                            waitListLabel,
                        },
                    },
                )}
            </h1>
            <p class="mt-8">
                {$_(
                    'We have sent a confirmation email, and will reach out when we start onboarding early access in Q1 2026.',
                )}
            </p>
            <p class="mt-8 opacity-75">
                {@html $_(
                    'You will be automatically redirected to<br/>{origin} in a few seconds.',
                    {
                        values: {
                            origin: `<a href="${returnUri}" class="underline">${hostname}</a>`,
                        },
                    },
                )}
            </p>
        {:else}
            <div class="md:px-8">
                <h1 class="text-center text-2xl font-medium leading-10">
                    {$_('{waitListLabel} Waitlist', {
                        values: {
                            waitListLabel,
                        },
                    })}
                </h1>
                <h2 class="mt-2 opacity-75">Coming Q1 2026</h2>
                <form onsubmit={preventDefault(() => submit())}>
                    <div id="email-wrapper" class="mt-10 w-full text-center">
                        <input
                            type="email"
                            name="email"
                            id="email"
                            autocomplete="email"
                            autocapitalize="off"
                            oninput={checkEmail}
                            bind:value={email}
                            placeholder={$_('enter your email')}
                            class="h-12 w-full bg-transparent px-[16px] sm:px-[18px]"
                        />

                        {#if checkEmailProviderAjax}
                            <div
                                class="mt-2 flex items-center justify-start"
                                transition:slide={{ duration: SLIDE_ANIM_MS }}
                            >
                                <SpinnerIcon
                                    css="h-5 w-5 text-charcoal dark:text-white"
                                />
                                <span class="ml-2 block text-left opacity-80"
                                    >{$_('Checking')}</span
                                >
                            </div>
                        {/if}

                        {#if isInvalidEmail}
                            {@const domain = email.split('@')[1]}
                            <span
                                data-test="disposable-email-error"
                                class="mt-2 block text-left text-red-500"
                                transition:slide={{ duration: SLIDE_ANIM_MS }}
                                >{$_('{domain} is an invalid domain', {
                                    values: { domain },
                                })}</span
                            >
                        {/if}

                        <button
                            data-test="email-send-verification-btn"
                            type="submit"
                            disabled={!isValidEmail(email) ||
                                ajaxRequestSend ||
                                checkEmailProviderAjax ||
                                isInvalidEmail ||
                                signUpError}
                            class="btn-background relative mt-3 inline-flex h-12 w-full items-center justify-center transition disabled:opacity-60"
                        >
                            {#if ajaxRequestSend}
                                <SpinnerIcon
                                    css="h-5 w-5 {showProviderPrompt
                                        ? 'text-charcoal dark:text-[#d4d4d4]'
                                        : 'text-white'}"
                                />
                            {:else}
                                {$_('Join the waitlist')}
                            {/if}
                        </button>
                        {#if signUpError}
                            <span
                                transition:slide={{ duration: SLIDE_ANIM_MS }}
                                class="mt-4 block text-center text-red-500"
                            >
                                {$_(
                                    'Something went wrong Please try again later',
                                )}
                            </span>
                        {/if}
                    </div>
                </form>
            </div>
        {/if}
    </main>
{/if}
